import { locale } from './locale.ts'

// Locale-bound formatting lives with the catalogues: the active language is decided here.

// Italian whatever the interface speaks: a bare 'en' resolves to en-US, month first. Cost:
// docs/sviluppo/trappole-frontend.md § «Un locale a due lettere passato a Intl sceglie la nazione»
const DATE_LOCALE = 'it-IT'

// One resolved formatter instead of one per call: `toLocaleDateString` re-resolves the
// locale and the option bag every time, and a table formats two dates per row.
const dayFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

// The instant formatter, for the columns where the hour is the whole point: a box last
// seen two minutes ago and one last seen at 00:05 are the same day and not the same news.
const instantFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

/** A value the API sends as a `date`, with no time in it at all: `2026-01-31`. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

// The branch is the trap, not a convenience: `new Date('2026-01-01')` is UTC midnight, so
// a date-only value renders as the day before anywhere behind UTC — invisible from Italy.
export function formatDate(iso: string): string {
  if (DATE_ONLY.test(iso)) {
    const [year, month, day] = iso.split('-')
    return dayFormat.format(new Date(Number(year), Number(month) - 1, Number(day)))
  }
  return dayFormat.format(new Date(iso))
}

/** A `date-time` with its hour kept, in the reader's own zone. Never for a `date`: there
 *  is no hour to show, and midnight UTC would be the day before — `formatDate` knows. */
export function formatDateTime(iso: string): string {
  return instantFormat.format(new Date(iso))
}

const weekdayFormats = {
  short: new Intl.DateTimeFormat(DATE_LOCALE, { weekday: 'short', timeZone: 'UTC' }),
  long: new Intl.DateTimeFormat(DATE_LOCALE, { weekday: 'long', timeZone: 'UTC' }),
} as const

/** An ISO weekday, 1 is Monday, as «lun» or «lunedì»: 2 November 2026 was a Monday. */
export function formatWeekday(isoWeekday: number, style: 'short' | 'long' = 'short'): string {
  return weekdayFormats[style].format(new Date(Date.UTC(2026, 10, 1 + isoWeekday)))
}

/** A whole hour of the day as a clock, «14:00». */
export function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`
}

const timeFormat = new Intl.DateTimeFormat(DATE_LOCALE, { hour: '2-digit', minute: '2-digit' })

/** The hour of a `date-time` alone, for a reading whose day goes on its own line. */
export function formatTime(iso: string): string {
  return timeFormat.format(new Date(iso))
}

/** The zone the FIGC schedules in. A kick-off is a civil Italian time, and the operator's
 *  laptop clock is not part of the contract — see `data/match-fields.ts`. */
export const KICKOFF_ZONE = 'Europe/Rome'

const kickOffFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: KICKOFF_ZONE,
})

/** A kick-off as the bulletin prints it. Pinned to Italy where `formatDateTime` is not:
 *  «last seen two minutes ago» is relative to the reader, a kick-off is not. */
export function formatKickOff(iso: string): string {
  return kickOffFormat.format(new Date(iso))
}

const kickOffTimeFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: KICKOFF_ZONE,
})

/** The clock of a kick-off alone, «15:00», for a row whose day is its section's heading. */
export function formatKickOffTime(iso: string): string {
  return kickOffTimeFormat.format(new Date(iso))
}

const kickOffDayFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: KICKOFF_ZONE,
})

/** The day of a kick-off as a heading, «sabato 31 ottobre». */
export function formatKickOffDay(iso: string): string {
  return kickOffDayFormat.format(new Date(iso))
}

const kickOffShortDateFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  timeZone: KICKOFF_ZONE,
})

/** The day of a kick-off in five characters, «31/10», for a giornata in the rail. */
export function formatKickOffShortDate(iso: string): string {
  return kickOffShortDateFormat.format(new Date(iso))
}

/** Today as a civil date, from the local parts. `toISOString().slice(0, 10)` is the UTC
 * day, which in Italy is yesterday before 02:00 in summer — the mirror of the trap above. */
export function todayIsoDate(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${String(now.getFullYear())}-${month}-${day}`
}

// A gigabyte and not a gibibyte: the number is read against a disk sold in GB.
const GIGABYTE = 1_000_000_000

const gigabyteFormat = new Intl.NumberFormat(locale, {
  style: 'unit',
  unit: 'gigabyte',
  maximumFractionDigits: 1,
})

export function formatGigabytes(bytes: number): string {
  return gigabyteFormat.format(bytes / GIGABYTE)
}

/** A size in the largest unit that stays readable. `formatGigabytes` is right for a disk,
 *  which is always tens of GB; a segment is kilobytes and a whole match is gigabytes, so a
 *  fixed unit reads «0 GB» over bytes that are really there. */
export function formatBytes(bytes: number): string {
  const [amount, unit] = byteParts(bytes)
  return scaledFormat(unit, 1).format(amount)
}

function byteParts(bytes: number): [number, 'byte' | 'kilobyte' | 'megabyte' | 'gigabyte'] {
  if (bytes < 1_000) return [bytes, 'byte']
  if (bytes < 1_000_000) return [bytes / 1_000, 'kilobyte']
  if (bytes < GIGABYTE) return [bytes / 1_000_000, 'megabyte']
  return [bytes / GIGABYTE, 'gigabyte']
}

/** The magnitude in the largest unit that stays readable — a fifteen-day skew in seconds
 *  is seven digits nobody parses. Unsigned on purpose: **the caller says which way**, in
 *  words, because «indietro» and «avanti» are two diagnoses and a minus sign is neither. */
export function formatDuration(seconds: number): string {
  const magnitude = Math.abs(seconds)
  const [amount, unit] = durationParts(magnitude)
  return scaledFormat(unit, 0).format(amount)
}

/** The two ladder formatters pick their unit at call time, so the file's «one resolved
 * formatter, not one per call» rule needs a cache rather than a constant. */
const scaledFormats = new Map<string, Intl.NumberFormat>()

function scaledFormat(unit: string, maximumFractionDigits: number): Intl.NumberFormat {
  const key = `${unit}/${String(maximumFractionDigits)}`
  const held = scaledFormats.get(key)
  if (held !== undefined) return held
  const made = new Intl.NumberFormat(locale, { style: 'unit', unit, maximumFractionDigits })
  scaledFormats.set(key, made)
  return made
}

function durationParts(seconds: number): [number, 'second' | 'minute' | 'hour' | 'day'] {
  if (seconds < 120) return [seconds, 'second']
  if (seconds < 7200) return [seconds / 60, 'minute']
  if (seconds < 172_800) return [seconds / 3600, 'hour']
  return [seconds / 86_400, 'day']
}
